import { Box, styled, useTheme } from '@chronosphereio/chrono-ui';
import type { LineSeriesOption } from 'echarts/charts';
import { useMemo } from 'react';
import { GridComponents, GridItemProps, VirtuosoGrid } from 'react-virtuoso';
import { StatChartBase, StatChartBaseProps } from '@/components';
import { StatChartThresholdOptions, UnitOptions, useFindThreshold } from '@/core';
import { CombinedStatChartData, STAT_CHART_MIN_WIDTH, StatChartColorScheme } from './stat-chart-model';
import { calculateSeriesDisplayProps } from './utils/data-transform';

const STAT_CHART_PANEL_PADDING_OFFSET = 16;

interface StatChartVirtualizedGridProps extends Pick<StatChartBaseProps, 'valueAlign'> {
  statChartData: CombinedStatChartData[];
  referenceSeries?: LineSeriesOption[];
  contentWidth: number;
  chartHeight: number;
  unit: UnitOptions;
  thresholds: StatChartThresholdOptions;
  isMultiSeries: boolean;
  valueFontSize?: StatChartBaseProps['valueFontSize'];
  roundValueFontDownToEven?: StatChartBaseProps['roundValueFontDownToEven'];
  seriesNameFontSize?: StatChartBaseProps['seriesNameFontSize'];
  descriptionFontSize?: StatChartBaseProps['descriptionFontSize'];
  messageFontSize?: StatChartBaseProps['messageFontSize'];
  description?: string;
  colorScheme: StatChartColorScheme;
  showSeriesName: boolean;
  comparisonPeriodLabel?: string;
}

export function StatChartVirtualizedGrid({
  statChartData,
  referenceSeries,
  contentWidth,
  chartHeight,
  unit,
  thresholds,
  isMultiSeries,
  valueFontSize,
  roundValueFontDownToEven,
  seriesNameFontSize,
  descriptionFontSize,
  messageFontSize,
  description,
  colorScheme,
  showSeriesName,
  comparisonPeriodLabel,
  valueAlign,
}: StatChartVirtualizedGridProps) {
  const findThreshold = useFindThreshold({ thresholds });
  const { theme } = useTheme();

  // Calculate the number of stat chart instances that can fit per row based on panel width.
  const columnCount = Math.max(1, Math.floor(contentWidth / (STAT_CHART_MIN_WIDTH + STAT_CHART_PANEL_PADDING_OFFSET)));
  const itemWidth = `${100 / columnCount}%`;

  // Calculate optimal chart width based on the actual column count and available width
  // Account for padding (px: 1 = 8px on each side = 16px total per item)
  const availableWidthPerItem = contentWidth / columnCount - STAT_CHART_PANEL_PADDING_OFFSET;
  const optimalChartWidth = Math.max(STAT_CHART_MIN_WIDTH, Math.floor(availableWidthPerItem));

  const gridComponents: GridComponents<CombinedStatChartData> = useMemo(
    () => ({
      List: ListContainer,
      Item: ({ children }: GridItemProps) => (
        <Box
          role="listitem"
          sx={{
            px: 1,
            mb: 0.5,
            width: itemWidth,
            minWidth: STAT_CHART_MIN_WIDTH,
            height: chartHeight,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>
      ),
    }),
    [itemWidth, chartHeight]
  );

  return (
    <VirtuosoGrid
      totalCount={statChartData.length}
      components={gridComponents}
      itemContent={(index) => {
        const series = statChartData[index];
        if (!series) return null;

        const { threshold, showSeriesNameAsMessage, calculatedShowSeriesName } = calculateSeriesDisplayProps({
          calculatedValue: series.calculatedValue,
          findThreshold,
          showSeriesName,
        });

        return (
          <InnerWrapper hasMultipleCharts={isMultiSeries}>
            <StatChartBase
              width={optimalChartWidth}
              height={chartHeight}
              data={series}
              unit={unit}
              color={colorScheme === 'thresholds' ? series.color : theme.palette.text.primary}
              sparkline={series.sparkline}
              referenceSeries={referenceSeries}
              isMultiSeries={isMultiSeries}
              valueFontSize={valueFontSize}
              roundValueFontDownToEven={roundValueFontDownToEven}
              seriesNameFontSize={seriesNameFontSize}
              descriptionFontSize={descriptionFontSize}
              messageFontSize={messageFontSize}
              comparison={series.comparison}
              comparisonPeriodLabel={comparisonPeriodLabel}
              description={description}
              message={showSeriesNameAsMessage ? series.seriesData?.name : threshold.name}
              hideValue={threshold.show_value === false}
              backgroundColor={threshold.background_color}
              showSeriesName={calculatedShowSeriesName}
              valueAlign={valueAlign}
            />
          </InnerWrapper>
        );
      }}
      computeItemKey={(seriesIndex) => `stat-chart-series-${seriesIndex}`}
      overscan={10}
    />
  );
}

const InnerWrapper = styled('div')<{ hasMultipleCharts: boolean }>(({ theme, hasMultipleCharts }) => ({
  padding: hasMultipleCharts ? theme.spacing(0.5) : 0,
}));

const ListContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});
